import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar
} from "recharts";
import { Leaf, Sparkles, Users, TrendingUp, Trophy, Trees, Award } from "lucide-react";

const MEDALS = ["#F59E0B", "#94A3B8", "#B45309"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/sustainability-leaderboard").then((r) => setLeaderboard(r.data || []));
  }, []);

  if (!stats) return <div>Loading…</div>;

  const rph = stats.rides_per_hour.map((c, h) => ({ hour: `${h}h`, rides: c }));
  const totalCo2 = leaderboard.reduce((s, d) => s + d.co2_kg, 0);
  const totalTrees = +(totalCo2 / 21.77).toFixed(1);
  const sharedTotal = leaderboard.reduce((s, d) => s + d.shared, 0);
  const totalRides = leaderboard.reduce((s, d) => s + d.rides, 0);
  const sharedPct = totalRides ? Math.round((sharedTotal / totalRides) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Insights</p>
          <h1 className="font-display font-bold text-3xl text-slate-900">Analytics & Reports</h1>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: TrendingUp, label: "Rides today", value: stats.rides_today, color: "text-primary" },
          { icon: Users, label: "Active employees", value: stats.employees, color: "text-slate-900" },
          { icon: Leaf, label: "CO₂ saved (month)", value: `${totalCo2.toFixed(1)} kg`, color: "text-emerald-600" },
          { icon: Sparkles, label: "Shared rides", value: `${sharedPct}%`, color: "text-amber-600" },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-5">
            <c.icon size={20} className={c.color} />
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-3">{c.label}</p>
            <p className="font-display font-bold text-3xl text-slate-900 mt-1">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">Hourly demand</p>
          <p className="font-display font-bold text-lg mt-1 mb-3">Rides per hour</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={rph}>
              <defs>
                <linearGradient id="cyanGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22B8E0" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#22B8E0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="hour" fontSize={10} stroke="#94a3b8" interval={2} />
              <YAxis fontSize={10} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Area type="monotone" dataKey="rides" stroke="#22B8E0" strokeWidth={2.5} fill="url(#cyanGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">CO₂ saved by department</p>
          <p className="font-display font-bold text-lg mt-1 mb-3">Greenest teams · this month</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={leaderboard} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" fontSize={10} stroke="#94a3b8" unit=" kg" />
              <YAxis dataKey="department" type="category" fontSize={11} stroke="#94a3b8" width={90} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} formatter={(v) => `${v} kg`} />
              <Bar dataKey="co2_kg" fill="#10B981" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Sustainability hero + leaderboard */}
      <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white rounded-3xl p-6 relative overflow-hidden" data-testid="sustainability-hero">
        <div className="absolute -right-16 -top-16 w-56 h-56 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute -right-6 -bottom-6 w-32 h-32 bg-white/5 rounded-full" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <Leaf size={32} />
            <span className="px-3 py-1 rounded-full bg-white/15 text-[10px] uppercase tracking-widest font-bold">Live ESG metric</span>
          </div>
          <h2 className="font-display font-bold text-3xl mt-3">Sustainability impact</h2>
          <p className="text-sm text-white/80 mt-1 max-w-md">Shared and bus rides across all ENEC teams measurably reduce transport emissions.</p>
          <div className="grid grid-cols-3 gap-4 mt-5">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">CO₂ saved (month)</p>
              <p className="font-display font-bold text-3xl">{totalCo2.toFixed(0)} kg</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Trees equivalent</p>
              <p className="font-display font-bold text-3xl flex items-center gap-1.5"><Trees size={22} />{totalTrees}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/70 font-bold">Shared %</p>
              <p className="font-display font-bold text-3xl">{sharedPct}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="dept-leaderboard">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Trophy size={18} className="text-amber-500" />
          <p className="font-display font-bold text-lg text-slate-900">Greenest Departments — this month</p>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              {["Rank", "Department", "CO₂ saved", "per Employee", "Total rides", "Shared", "Bus seats"].map(h => (
                <th key={h} className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-bold px-5 py-3 border-b border-slate-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leaderboard.map((d, i) => (
              <tr key={d.department} data-testid={`leaderboard-row-${d.department}`} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    {i < 3 ? (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: MEDALS[i] }}>
                        <Award size={14} />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-sm">{i + 1}</div>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-sm font-display font-bold text-slate-900">{d.department}</td>
                <td className="px-5 py-3 text-sm font-mono font-bold text-emerald-600">{d.co2_kg} kg</td>
                <td className="px-5 py-3 text-sm font-mono text-slate-700">{d.co2_per_employee} kg</td>
                <td className="px-5 py-3 text-sm text-slate-700">{d.rides}</td>
                <td className="px-5 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-bold uppercase tracking-wider">{d.shared}</span></td>
                <td className="px-5 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold uppercase tracking-wider">{d.bus}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {leaderboard.length === 0 && (
          <div className="px-5 py-8 text-center text-slate-400 text-sm">No completed rides this month yet.</div>
        )}
      </div>
    </div>
  );
}
